import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Trash2, Save, Loader2, ArrowLeft, Users, Clock,
  AlertCircle, CheckCircle2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERVIEW_TYPES = ['Phone', 'Video', 'In-Person'];

/**
 * ScheduleTemplates — admin page to configure per-round interview templates.
 *
 * Two scopes exist:
 *   - "global"       — applies to applicants that don't have a posting-specific override
 *   - "job_posting"  — per-posting override (one template per posting)
 *
 * Each template is just an ordered list of rounds (e.g. 1st, 2nd, Final),
 * and each round picks which interviewers from your is_interviewer roster
 * are eligible for that round.
 */
function RoundEditor({ round, onChange, onRemove, interviewers }) {
  const toggleInterviewer = (id) => {
    const current = round.interviewer_user_ids || [];
    const next = current.includes(id)
      ? current.filter(x => x !== id)
      : [...current, id];
    onChange({ ...round, interviewer_user_ids: next });
  };

  const selected = new Set(round.interviewer_user_ids || []);

  return (
    <Card className="border-muted" data-testid={`round-card-${round.round_num}`}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-primary text-primary-foreground text-sm font-bold">
            {round.round_num}
          </span>
          Round
        </CardTitle>
        <Button
          variant="ghost"
          size="sm"
          onClick={onRemove}
          className="text-destructive hover:text-destructive hover:bg-destructive/10"
          data-testid={`round-remove-${round.round_num}`}
        >
          <Trash2 size={14} />
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-muted-foreground">Name</label>
            <Input
              value={round.name}
              onChange={e => onChange({ ...round, name: e.target.value })}
              placeholder="1st, 2nd, Final…"
              data-testid={`round-name-${round.round_num}`}
            />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Duration (min)</label>
            <Input
              type="number"
              min={5}
              max={240}
              step={5}
              value={round.duration_minutes}
              onChange={e => onChange({ ...round, duration_minutes: parseInt(e.target.value, 10) || 30 })}
              data-testid={`round-duration-${round.round_num}`}
            />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Type</label>
            <select
              value={round.interview_type}
              onChange={e => onChange({ ...round, interview_type: e.target.value })}
              className="w-full h-9 px-3 text-sm border rounded-md bg-background"
              data-testid={`round-type-${round.round_num}`}
            >
              {INTERVIEW_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Location</label>
            <Input
              value={round.location}
              onChange={e => onChange({ ...round, location: e.target.value })}
              placeholder="Office / Google Meet / phone…"
              data-testid={`round-location-${round.round_num}`}
            />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-xs font-medium text-muted-foreground inline-flex items-center gap-1.5">
              <Users size={12} />
              Eligible interviewers
            </label>
            <span className="text-xs text-muted-foreground">
              {selected.size} selected
            </span>
          </div>
          {interviewers.length === 0 ? (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
              No personnel are marked as interviewers yet. Add them in <b>Personnel → Edit → Is Interviewer</b>.
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-1.5 max-h-44 overflow-y-auto border rounded-md p-2">
              {interviewers.map(p => (
                <label
                  key={p.id}
                  className={`flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer hover:bg-muted/50 text-sm ${selected.has(p.id) ? 'bg-primary/5' : ''}`}
                  data-testid={`round-${round.round_num}-interviewer-${p.id}`}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(p.id)}
                    onChange={() => toggleInterviewer(p.id)}
                  />
                  <span className="truncate">
                    {p.first_name} {p.last_name}
                    {p.discipline && <span className="text-xs text-muted-foreground ml-1">· {p.discipline}</span>}
                  </span>
                </label>
              ))}
            </div>
          )}
          {selected.size === 0 && (
            <p className="text-xs text-amber-700 mt-1.5 flex items-center gap-1">
              <AlertCircle size={12} />
              No interviewers selected — applicants for this round will have no one to book with.
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function ScheduleTemplates() {
  const [scope, setScope] = useState('global');
  const [jobPostingId, setJobPostingId] = useState('');
  const [postings, setPostings] = useState([]);
  const [interviewers, setInterviewers] = useState([]);
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadInterviewers = async () => {
    try {
      const res = await axios.get(`${API}/admin/interviewers`);
      setInterviewers(res.data || []);
    } catch (e) {
      toast.error(`Failed to load interviewers: ${e.response?.data?.detail || e.message}`);
    }
  };

  const loadPostings = async () => {
    try {
      const res = await axios.get(`${API}/admin/job-positions`);
      setPostings(res.data || []);
    } catch (e) {
      // Non-fatal
      setPostings([]);
    }
  };

  const loadTemplate = async () => {
    setLoading(true);
    try {
      const params = { job_posting_id: scope === 'job_posting' ? jobPostingId : undefined };
      const res = await axios.get(`${API}/admin/schedule-templates/effective`, { params });
      const rounds = res.data?.rounds || [];
      setTemplate({
        ...res.data,
        rounds: rounds.map(r => ({ ...r })),
      });
    } catch (e) {
      toast.error(`Failed to load template: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadInterviewers(); loadPostings(); }, []);
  useEffect(() => {
    if (scope === 'job_posting' && !jobPostingId) {
      setLoading(false);
      setTemplate(null);
      return;
    }
    loadTemplate();
  }, [scope, jobPostingId]);

  const addRound = () => {
    if (!template) return;
    const nextNum = (template.rounds || []).reduce((m, r) => Math.max(m, r.round_num || 0), 0) + 1;
    setTemplate({
      ...template,
      rounds: [...(template.rounds || []), {
        name: `Round ${nextNum}`,
        round_num: nextNum,
        duration_minutes: 30,
        interview_type: 'Phone',
        location: '',
        interviewer_user_ids: [],
      }],
    });
  };

  const updateRound = (idx, updated) => {
    setTemplate(t => ({
      ...t,
      rounds: t.rounds.map((r, i) => i === idx ? updated : r),
    }));
  };

  const removeRound = (idx) => {
    setTemplate(t => ({ ...t, rounds: t.rounds.filter((_, i) => i !== idx) }));
  };

  const handleSave = async () => {
    if (!template || !(template.rounds || []).length) {
      toast.error('Add at least one round before saving');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/admin/schedule-templates`, {
        scope,
        job_posting_id: scope === 'job_posting' ? jobPostingId : null,
        rounds: template.rounds,
      });
      setTemplate(res.data);
      toast.success(`${scope === 'global' ? 'Global' : 'Posting-specific'} template saved`);
    } catch (e) {
      toast.error(`Save failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 p-6 max-w-5xl mx-auto" data-testid="schedule-templates-page">
      <div>
        <Button variant="ghost" size="sm" onClick={() => window.history.back()} className="mb-2 gap-1.5">
          <ArrowLeft size={14} /> Back
        </Button>
        <h1 className="text-3xl font-bold">Interview Schedule Templates</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Define interview rounds and pick which interviewers are eligible for each round.
          Applicants only see the eligible interviewers when they self-schedule.
        </p>
      </div>

      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-3">
            <label className="text-sm font-medium">Scope</label>
            <select
              value={scope}
              onChange={e => setScope(e.target.value)}
              className="h-9 px-3 text-sm border rounded-md bg-background"
              data-testid="schedule-scope-select"
            >
              <option value="global">Global (default for all postings)</option>
              <option value="job_posting">Per job posting (override)</option>
            </select>
            {scope === 'job_posting' && (
              <select
                value={jobPostingId}
                onChange={e => setJobPostingId(e.target.value)}
                className="h-9 px-3 text-sm border rounded-md bg-background flex-1"
                data-testid="schedule-posting-select"
              >
                <option value="">Select job posting…</option>
                {postings.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.title}{p.discipline ? ` (${p.discipline})` : ''}
                  </option>
                ))}
              </select>
            )}
          </div>
          {template?.synthesized && (
            <div className="text-xs text-muted-foreground inline-flex items-center gap-1">
              <CheckCircle2 size={12} className="text-emerald-600" />
              Showing the default — click Save to make it real.
            </div>
          )}
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="animate-spin mr-2" /> Loading…
        </div>
      ) : scope === 'job_posting' && !jobPostingId ? (
        <div className="text-center py-16 text-muted-foreground">
          Pick a job posting to view or override its schedule template.
        </div>
      ) : template ? (
        <>
          <div className="space-y-3">
            {(template.rounds || []).map((r, idx) => (
              <RoundEditor
                key={idx}
                round={r}
                onChange={u => updateRound(idx, u)}
                onRemove={() => removeRound(idx)}
                interviewers={interviewers}
              />
            ))}
          </div>
          <div className="flex items-center justify-between">
            <Button variant="outline" onClick={addRound} className="gap-2" data-testid="add-round-btn">
              <Plus size={14} /> Add round
            </Button>
            <Button onClick={handleSave} disabled={saving} className="gap-2" data-testid="save-template-btn">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {saving ? 'Saving…' : 'Save template'}
            </Button>
          </div>
        </>
      ) : null}
    </div>
  );
}
